import React from 'react';
import styled from 'styled-components';

interface LoadingBarProps {
    text?: string;
    duration?: number;
    color?: 'green' | 'purple' | 'blue' | 'orange';
    size?: 'small' | 'medium' | 'large';
}

const LoadingContainer = styled.div`
    margin: 20px 0;
    text-align: center;
`;

const LoadingText = styled.div<{ color: string }>`
    color: ${(props) => props.color};
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 10px;
`;

const LoadingBarTrack = styled.div<{ size: string }>`
    width: 100%;
    max-width: ${(props) => {
        switch (props.size) {
            case 'small':
                return '200px';
            case 'large':
                return '400px';
            default:
                return '300px';
        }
    }};
    height: ${(props) => {
        switch (props.size) {
            case 'small':
                return '4px';
            case 'large':
                return '8px';
            default:
                return '6px';
        }
    }};
    background: ${(props) => props.color}20;
    border-radius: ${(props) => {
        switch (props.size) {
            case 'small':
                return '2px';
            case 'large':
                return '4px';
            default:
                return '3px';
        }
    }};
    margin: 0 auto;
    overflow: hidden;
`;

const LoadingBarFill = styled.div<{
    duration: number;
    color: string;
}>`
    height: 100%;
    background: ${(props) => props.color};
    border-radius: inherit;
    animation: loadingProgress ${(props) => props.duration}s ease-out forwards;
    width: 0%;

    @keyframes loadingProgress {
        from {
            width: 0%;
        }
        to {
            width: 100%;
        }
    }
`;

const LoadingBar: React.FC<LoadingBarProps> = ({
    text = 'Loading...',
    duration = 1.5,
    color = 'green',
    size = 'medium',
}) => {
    const getColorValue = () => {
        switch (color) {
            case 'purple':
                return '#7c3aed';
            case 'blue':
                return '#3b82f6';
            case 'orange':
                return '#f59e0b';
            default:
                return '#16a34a';
        }
    };

    const getGradientColor = () => {
        switch (color) {
            case 'purple':
                return 'linear-gradient(90deg, #7c3aed 0%, #8b5cf6 100%)';
            case 'blue':
                return 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)';
            case 'orange':
                return 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)';
            default:
                return 'linear-gradient(90deg, #16a34a 0%, #22c55e 100%)';
        }
    };

    const colorValue = getColorValue();
    const gradientValue = getGradientColor();

    return (
        <LoadingContainer>
            <LoadingText color={colorValue}>{text}</LoadingText>
            <LoadingBarTrack size={size} color={colorValue}>
                <LoadingBarFill duration={duration} color={gradientValue} />
            </LoadingBarTrack>
        </LoadingContainer>
    );
};

LoadingBar.defaultProps = {
    text: 'Loading...',
    duration: 1.5,
    color: 'green',
    size: 'medium',
};

export default LoadingBar;
